import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { ErrorCode, IError } from '@app/models/error';
import { RetrieveWarehouseSchema } from '@app/models/wms';
import Api from '@app/services/api';
import { getUnfinishedOrdersError, hasErrorCode } from '@app/services/apiErrors';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';

type TransitionMessage = 'deactivateSuccess' | 'reactivateSuccess' | 'alreadyDeactivated' | 'providerDeactivated';

const useWarehouseTransition = (
  request: (uid: string) => Promise<RetrieveWarehouseSchema>,
  successMessage: TransitionMessage,
  conflictMessage: TransitionMessage,
  onUnfinishedOrders?: (error: IError) => void
) => {
  const client = useQueryClient();
  const warehousesT = useTranslation(NSKeys.warehouses);
  const getErrorMessage = useApiErrorMessage();

  return useMutation({
    mutationFn: request,
    onSuccess: () => message.success(warehousesT.t(successMessage)),
    onError: (err) => {
      const unfinishedOrders = getUnfinishedOrdersError(err);
      if (unfinishedOrders && onUnfinishedOrders) {
        onUnfinishedOrders(unfinishedOrders);
        return;
      }
      message.error(
        hasErrorCode(err, [ErrorCode.WAREHOUSE_TRANSITION_FORBIDDEN])
          ? warehousesT.t(conflictMessage)
          : getErrorMessage(err)
      );
    },
    onSettled: (_data, _err, uid) =>
      Promise.all([client.invalidateQueries(['getWarehouses']), client.invalidateQueries(['getWarehouse', uid])]),
  });
};

export const useDeactivateWarehouseQuery = (onUnfinishedOrders?: (error: IError) => void) =>
  useWarehouseTransition(
    (uid) => Api.wms.deactivateWarehouse(uid),
    'deactivateSuccess',
    'alreadyDeactivated',
    onUnfinishedOrders
  );

export const useReactivateWarehouseQuery = () =>
  useWarehouseTransition((uid) => Api.wms.reactivateWarehouse(uid), 'reactivateSuccess', 'providerDeactivated');
